import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bars3Icon, DevicePhoneMobileIcon, GlobeAltIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../LanguageContext';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.rightoz.user';
const IOS_APP_URL = 'https://apps.apple.com/in/app/rightoz-consumer/id6763323665';
const WEB_APP_URL = 'https://www.consumer.rightoz.in/';

const navigation = {
  en: ['Follow Your Milk', 'Video', 'Join Us As Partner'],
  hi: ['अपने दूध का सफ़र', 'वीडियो', 'पार्टनर के रूप में जुड़ें'],
};

const navigationLinks = ['journey', 'video', 'join-us'];

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const currentLanguage = language;
  const links = navigation[currentLanguage].map((name, index) => ({
    name,
    href: `/#${navigationLinks[index]}`,
  }));

  useEffect(() => {
    if (!isSubscriptionOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsSubscriptionOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isSubscriptionOpen]);

  useEffect(() => {
    const openSubscription = () => {
      setIsOpen(false);
      setIsSubscriptionOpen(true);
    };
    window.addEventListener('rightoz:open-subscription', openSubscription);
    return () => window.removeEventListener('rightoz:open-subscription', openSubscription);
  }, []);

  const openSubscription = () => {
    setIsOpen(false);
    setIsSubscriptionOpen(true);
  };

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-[rgba(27,94,32,0.08)] bg-[rgba(255,255,255,0.94)] shadow-sm backdrop-blur-xl transition-all duration-300"
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <a href="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="Rightoz Logo"
              className="h-10 w-auto"
            />
          </a>

          <div className="hidden items-center gap-2 lg:flex">
            {links.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="rounded-full px-4 py-2 text-sm text-[var(--color-forest)] transition hover:bg-[rgba(27,94,32,0.08)]"
              >
                {item.name}
              </a>
            ))}
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            <div
              className="inline-flex rounded-full border border-[rgba(27,94,32,0.14)] bg-[var(--color-cream)] p-1"
              aria-label="Language"
            >
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  currentLanguage === 'en'
                    ? 'bg-[var(--color-forest)] text-white shadow-sm'
                    : 'text-[var(--color-forest)] hover:bg-white'
                }`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLanguage('hi')}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  currentLanguage === 'hi'
                    ? 'bg-[var(--color-forest)] text-white shadow-sm'
                    : 'text-[var(--color-forest)] hover:bg-white'
                }`}
              >
                हिन्दी
              </button>
            </div>
            <button
              type="button"
              onClick={openSubscription}
              className="cta-glow inline-flex items-center justify-center rounded-full bg-[var(--color-forest)] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#174f1b]"
            >
              {currentLanguage === 'en' ? 'Start Subscription' : 'सदस्यता शुरू करें'}
            </button>
          </div>

          <div className="flex items-center gap-1 lg:hidden">
            <div
              className="inline-flex rounded-full border border-[rgba(27,94,32,0.14)] bg-[var(--color-cream)] p-1"
              aria-label="Language"
            >
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`rounded-full px-2.5 py-1.5 text-xs font-semibold transition ${
                  currentLanguage === 'en'
                    ? 'bg-[var(--color-forest)] text-white shadow-sm'
                    : 'text-[var(--color-forest)]'
                }`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLanguage('hi')}
                className={`rounded-full px-2.5 py-1.5 text-xs font-semibold transition ${
                  currentLanguage === 'hi'
                    ? 'bg-[var(--color-forest)] text-white shadow-sm'
                    : 'text-[var(--color-forest)]'
                }`}
              >
                हिन्दी
              </button>
            </div>
            <button
              onClick={() => setIsOpen((open) => !open)}
              className="rounded-full p-2 text-[var(--color-forest)]"
              aria-label={currentLanguage === 'hi' ? 'मेन्यू खोलें या बंद करें' : 'Toggle menu'}
            >
              {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="mb-4 rounded-[1.75rem] border border-[rgba(27,94,32,0.08)] bg-[rgba(248,246,241,0.98)] p-4 shadow-lg lg:hidden">
            <div className="space-y-2">
              {links.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block rounded-2xl px-4 py-3 text-sm text-[var(--color-ink)] transition hover:bg-[rgba(27,94,32,0.06)]"
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              <button
                type="button"
                onClick={openSubscription}
                className="cta-glow mt-2 block rounded-2xl bg-[var(--color-forest)] px-4 py-3 text-center text-sm font-semibold text-white"
              >
                {currentLanguage === 'en' ? 'Start Subscription' : 'सदस्यता शुरू करें'}
              </button>
            </div>
          </div>
        )}
      </nav>

      {isSubscriptionOpen && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-[rgba(8,35,12,0.58)] p-4 backdrop-blur-sm sm:items-center sm:py-8"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsSubscriptionOpen(false);
          }}
        >
          <div
            className="my-auto max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-[2rem] bg-white p-6 shadow-[0_24px_80px_rgba(0,0,0,0.25)] sm:max-h-[calc(100vh-4rem)] sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="subscription-dialog-title"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--color-forest)]">Rightoz</p>
                <h2 id="subscription-dialog-title" className="mt-2 font-display text-3xl text-[var(--color-ink)]">
                  {currentLanguage === 'en' ? 'Choose how to subscribe' : 'सदस्यता का तरीका चुनें'}
                </h2>
                <p className="mt-2 text-sm leading-6 text-[var(--color-muted)]">
                  {currentLanguage === 'en' ? 'Continue with the app or open the web experience' : 'ऐप या वेब अनुभव से आगे बढ़ें।'}
                </p>
              </div>
              <button type="button" onClick={() => setIsSubscriptionOpen(false)} className="rounded-full p-2 text-[var(--color-muted)] transition hover:bg-[var(--color-cream)] hover:text-[var(--color-forest)]" aria-label="Close dialog">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mt-6 grid gap-3">
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-2xl border border-[rgba(27,94,32,0.14)] bg-[var(--color-cream)] p-4 font-semibold text-[var(--color-forest)] transition hover:-translate-y-0.5 hover:border-[var(--color-forest)]">
                <DevicePhoneMobileIcon className="h-7 w-7" />
                <span><span className="block">Android</span><span className="text-xs font-normal text-[var(--color-muted)]">Get it on Google Play</span></span>
                <span className="ml-auto">→</span>
              </a>
              <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-2xl border border-[rgba(27,94,32,0.14)] bg-[var(--color-cream)] p-4 font-semibold text-[var(--color-forest)] transition hover:-translate-y-0.5 hover:border-[var(--color-forest)]">
                <DevicePhoneMobileIcon className="h-7 w-7" />
                <span><span className="block">iOS</span><span className="text-xs font-normal text-[var(--color-muted)]">Download on the App Store</span></span>
                <span className="ml-auto">→</span>
              </a>
              <a href={WEB_APP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-2xl border border-[rgba(27,94,32,0.14)] bg-[var(--color-cream)] p-4 font-semibold text-[var(--color-forest)] transition hover:-translate-y-0.5 hover:border-[var(--color-forest)]">
                <GlobeAltIcon className="h-7 w-7" />
                <span><span className="block">Web app</span><span className="text-xs font-normal text-[var(--color-muted)]">Open in your browser</span></span>
                <span className="ml-auto">→</span>
              </a>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
};

export default Header;
